import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import { useLang } from '../i18n';

const css = `
  .legal { padding: 6px 16px 32px; }
  .legal-hero { margin-bottom: 20px; padding: 16px 14px; border-radius: 12px; background: linear-gradient(135deg, rgba(129,135,246,.15), rgba(118,225,255,.06)); box-shadow: inset 0 0 0 1px rgba(118,225,255,.18); }
  .legal-hero-title { font-size: 17px; font-weight: 800; color: #fff; }
  .legal-hero-sub { font-size: 12px; color: #76e1ff; margin-top: 4px; }
  .legal-section { margin-top: 18px; }
  .legal-section-title { font-size: 13px; font-weight: 800; color: #fff; margin-bottom: 8px; letter-spacing: .2px; display: flex; align-items: center; gap: 7px; }
  .legal-section-title::before { content: ''; display: block; width: 3px; height: 14px; border-radius: 2px; background: linear-gradient(#8187f6, #76e1ff); flex: none; }
  .legal-p { font-size: 12.5px; color: #9b9ab0; line-height: 1.65; }
  .legal-p + .legal-p { margin-top: 8px; }
  .legal-ul { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  .legal-ul li { font-size: 12.5px; color: #9b9ab0; padding-left: 14px; position: relative; line-height: 1.5; }
  .legal-ul li::before { content: '—'; position: absolute; left: 0; color: #76e1ff; }
  .legal-tag { display: inline-flex; align-items: center; gap: 5px; margin-top: 14px; padding: 6px 12px; border-radius: 8px; background: rgba(0,210,106,.1); color: #00d26a; font-size: 11px; font-weight: 700; }
  .legal-date { font-size: 11px; color: #5a5972; margin-top: 28px; text-align: center; }
`;

const CONTENT = {
  fr: {
    backTitle: 'Politique de confidentialité',
    heroTitle: 'Politique de confidentialité',
    heroSub: 'Conformité RGPD — Mise à jour : Janvier 2025',
    tag: 'Protection des données — RGPD',
    footer: '© 2025 Roxs Sports — Tous droits réservés',
    sections: [
      {
        title: '1. Responsable du traitement',
        body: [
          'Roxs Sports est le responsable du traitement de vos données personnelles, collectées dans le cadre de l\'utilisation de notre plateforme de paris sportifs.',
          'Pour toute question relative à la protection de vos données, vous pouvez nous contacter via la messagerie de support intégrée à l\'application.',
        ],
      },
      {
        title: '2. Données collectées',
        body: ['Nous collectons les données suivantes lors de l\'utilisation de nos services :'],
        list: [
          'Données d\'identité : nom, prénom, date de naissance',
          'Données de contact : adresse e-mail, numéro de téléphone',
          'Données financières : historique des transactions, mode de paiement (non les numéros de carte)',
          'Données de navigation : adresse IP, type d\'appareil, pages visitées',
          'Données de jeu : paris placés, historique des mises et gains',
        ],
      },
      {
        title: '3. Finalités du traitement',
        body: ['Vos données personnelles sont utilisées aux fins suivantes :'],
        list: [
          'Création et gestion de votre compte',
          'Traitement des transactions financières',
          'Vérification d\'identité (KYC) conformément aux obligations légales',
          'Prévention de la fraude et du blanchiment d\'argent',
          'Amélioration de nos services et personnalisation de l\'expérience utilisateur',
          'Communication d\'informations relatives à votre compte',
        ],
      },
      {
        title: '4. Base légale',
        body: [
          'Le traitement de vos données repose sur : (1) l\'exécution du contrat nous liant à vous en tant qu\'utilisateur, (2) le respect d\'obligations légales auxquelles Roxs Sports est soumis, (3) votre consentement explicite pour les communications marketing.',
        ],
      },
      {
        title: '5. Durée de conservation',
        body: [
          'Vos données sont conservées pendant toute la durée de votre relation contractuelle avec Roxs Sports, et au-delà pendant les durées légalement requises (généralement 5 à 10 ans pour les données financières).',
          'Les données de navigation sont conservées pendant 13 mois maximum.',
        ],
      },
      {
        title: '6. Partage des données',
        body: ['Roxs Sports ne vend pas vos données personnelles à des tiers. Nous pouvons partager vos données avec :'],
        list: [
          'Nos prestataires de paiement pour traiter vos transactions',
          'Les autorités compétentes dans le cadre d\'obligations légales',
          'Nos prestataires techniques (hébergement, sécurité) sous contrat de confidentialité',
        ],
      },
      {
        title: '7. Vos droits (RGPD)',
        body: ['Conformément au Règlement Général sur la Protection des Données, vous disposez des droits suivants :'],
        list: [
          'Droit d\'accès à vos données personnelles',
          'Droit de rectification des données inexactes',
          'Droit à l\'effacement ("droit à l\'oubli")',
          'Droit à la portabilité de vos données',
          'Droit d\'opposition au traitement',
          'Droit à la limitation du traitement',
        ],
      },
      {
        title: '8. Cookies',
        body: [
          'Nous utilisons des cookies techniques nécessaires au fonctionnement de l\'application, ainsi que des cookies analytiques (anonymisés) pour améliorer notre service.',
          'Aucun cookie publicitaire tiers n\'est utilisé sans votre consentement explicite.',
        ],
      },
      {
        title: '9. Sécurité',
        body: [
          'Roxs Sports met en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données contre tout accès non autorisé, perte ou destruction. L\'ensemble des échanges est chiffré via TLS/HTTPS.',
        ],
      },
    ],
  },
  es: {
    backTitle: 'Política de privacidad',
    heroTitle: 'Política de privacidad',
    heroSub: 'Conformidad con el RGPD — Actualización: Enero de 2025',
    tag: 'Protección de datos — RGPD',
    footer: '© 2025 Roxs Sports — Todos los derechos reservados',
    sections: [
      {
        title: '1. Responsable del tratamiento',
        body: [
          'Roxs Sports es el responsable del tratamiento de sus datos personales, recopilados en el marco del uso de nuestra plataforma de apuestas deportivas.',
          'Para cualquier consulta relacionada con la protección de sus datos, puede contactarnos a través de la mensajería de soporte integrada en la aplicación.',
        ],
      },
      {
        title: '2. Datos recopilados',
        body: ['Recopilamos los siguientes datos durante el uso de nuestros servicios:'],
        list: [
          'Datos de identidad: nombre, apellido, fecha de nacimiento',
          'Datos de contacto: dirección de correo electrónico, número de teléfono',
          'Datos financieros: historial de transacciones, método de pago (no los números de tarjeta)',
          'Datos de navegación: dirección IP, tipo de dispositivo, páginas visitadas',
          'Datos de juego: apuestas realizadas, historial de apuestas y ganancias',
        ],
      },
      {
        title: '3. Finalidades del tratamiento',
        body: ['Sus datos personales se utilizan con los siguientes fines:'],
        list: [
          'Creación y gestión de su cuenta',
          'Procesamiento de las transacciones financieras',
          'Verificación de identidad (KYC) conforme a las obligaciones legales',
          'Prevención del fraude y del blanqueo de capitales',
          'Mejora de nuestros servicios y personalización de la experiencia de usuario',
          'Comunicación de información relativa a su cuenta',
        ],
      },
      {
        title: '4. Base legal',
        body: [
          'El tratamiento de sus datos se basa en: (1) la ejecución del contrato que nos vincula a usted como usuario, (2) el cumplimiento de las obligaciones legales a las que está sujeta Roxs Sports, (3) su consentimiento explícito para las comunicaciones de marketing.',
        ],
      },
      {
        title: '5. Plazo de conservación',
        body: [
          'Sus datos se conservan durante toda la duración de su relación contractual con Roxs Sports, y posteriormente durante los plazos legalmente exigidos (generalmente de 5 a 10 años para los datos financieros).',
          'Los datos de navegación se conservan durante un máximo de 13 meses.',
        ],
      },
      {
        title: '6. Compartición de datos',
        body: ['Roxs Sports no vende sus datos personales a terceros. Podemos compartir sus datos con:'],
        list: [
          'Nuestros proveedores de pago para procesar sus transacciones',
          'Las autoridades competentes en el marco de obligaciones legales',
          'Nuestros proveedores técnicos (alojamiento, seguridad) bajo contrato de confidencialidad',
        ],
      },
      {
        title: '7. Sus derechos (RGPD)',
        body: ['Conforme al Reglamento General de Protección de Datos, usted dispone de los siguientes derechos:'],
        list: [
          'Derecho de acceso a sus datos personales',
          'Derecho de rectificación de los datos inexactos',
          'Derecho de supresión ("derecho al olvido")',
          'Derecho a la portabilidad de sus datos',
          'Derecho de oposición al tratamiento',
          'Derecho a la limitación del tratamiento',
        ],
      },
      {
        title: '8. Cookies',
        body: [
          'Utilizamos cookies técnicas necesarias para el funcionamiento de la aplicación, así como cookies analíticas (anonimizadas) para mejorar nuestro servicio.',
          'No se utiliza ninguna cookie publicitaria de terceros sin su consentimiento explícito.',
        ],
      },
      {
        title: '9. Seguridad',
        body: [
          'Roxs Sports aplica medidas de seguridad técnicas y organizativas para proteger sus datos contra cualquier acceso no autorizado, pérdida o destrucción. Todos los intercambios están cifrados mediante TLS/HTTPS.',
        ],
      },
    ],
  },
  pt: {
    backTitle: 'Política de privacidade',
    heroTitle: 'Política de privacidade',
    heroSub: 'Conformidade com a LGPD/GDPR — Atualização: Janeiro de 2025',
    tag: 'Proteção de dados — LGPD/GDPR',
    footer: '© 2025 Roxs Sports — Todos os direitos reservados',
    sections: [
      {
        title: '1. Responsável pelo tratamento',
        body: [
          'A Roxs Sports é a responsável pelo tratamento dos seus dados pessoais, coletados no âmbito do uso da nossa plataforma de apostas esportivas.',
          'Para qualquer dúvida relacionada à proteção dos seus dados, você pode nos contatar pelo chat de suporte integrado ao aplicativo.',
        ],
      },
      {
        title: '2. Dados coletados',
        body: ['Coletamos os seguintes dados durante o uso dos nossos serviços:'],
        list: [
          'Dados de identidade: nome, sobrenome, data de nascimento',
          'Dados de contato: endereço de e-mail, número de telefone',
          'Dados financeiros: histórico de transações, método de pagamento (não os números do cartão)',
          'Dados de navegação: endereço IP, tipo de dispositivo, páginas visitadas',
          'Dados de jogo: apostas realizadas, histórico de apostas e ganhos',
        ],
      },
      {
        title: '3. Finalidades do tratamento',
        body: ['Seus dados pessoais são utilizados para as seguintes finalidades:'],
        list: [
          'Criação e gestão da sua conta',
          'Processamento das transações financeiras',
          'Verificação de identidade (KYC) conforme as obrigações legais',
          'Prevenção à fraude e à lavagem de dinheiro',
          'Melhoria dos nossos serviços e personalização da experiência do usuário',
          'Comunicação de informações relativas à sua conta',
        ],
      },
      {
        title: '4. Base legal',
        body: [
          'O tratamento dos seus dados baseia-se em: (1) a execução do contrato que nos vincula a você como usuário, (2) o cumprimento das obrigações legais às quais a Roxs Sports está sujeita, (3) o seu consentimento explícito para as comunicações de marketing.',
        ],
      },
      {
        title: '5. Prazo de conservação',
        body: [
          'Seus dados são conservados durante toda a duração da sua relação contratual com a Roxs Sports e, posteriormente, pelos prazos legalmente exigidos (geralmente de 5 a 10 anos para os dados financeiros).',
          'Os dados de navegação são conservados por no máximo 13 meses.',
        ],
      },
      {
        title: '6. Compartilhamento de dados',
        body: ['A Roxs Sports não vende seus dados pessoais a terceiros. Podemos compartilhar seus dados com:'],
        list: [
          'Nossos provedores de pagamento para processar suas transações',
          'As autoridades competentes no âmbito de obrigações legais',
          'Nossos prestadores técnicos (hospedagem, segurança) sob contrato de confidencialidade',
        ],
      },
      {
        title: '7. Seus direitos (LGPD/GDPR)',
        body: ['De acordo com a legislação de proteção de dados, você dispõe dos seguintes direitos:'],
        list: [
          'Direito de acesso aos seus dados pessoais',
          'Direito de retificação dos dados incorretos',
          'Direito à eliminação ("direito ao esquecimento")',
          'Direito à portabilidade dos seus dados',
          'Direito de oposição ao tratamento',
          'Direito à limitação do tratamento',
        ],
      },
      {
        title: '8. Cookies',
        body: [
          'Utilizamos cookies técnicos necessários ao funcionamento do aplicativo, bem como cookies analíticos (anonimizados) para melhorar nosso serviço.',
          'Nenhum cookie publicitário de terceiros é utilizado sem o seu consentimento explícito.',
        ],
      },
      {
        title: '9. Segurança',
        body: [
          'A Roxs Sports implementa medidas de segurança técnicas e organizacionais para proteger seus dados contra qualquer acesso não autorizado, perda ou destruição. Todas as trocas são criptografadas via TLS/HTTPS.',
        ],
      },
    ],
  },
  en: {
    backTitle: 'Privacy policy',
    heroTitle: 'Privacy policy',
    heroSub: 'GDPR compliance — Updated: January 2025',
    tag: 'Data protection — GDPR',
    footer: '© 2025 Roxs Sports — All rights reserved',
    sections: [
      {
        title: '1. Data controller',
        body: [
          'Roxs Sports is the controller of your personal data, collected as part of the use of our sports betting platform.',
          'For any question relating to the protection of your data, you can contact us via the support messaging built into the app.',
        ],
      },
      {
        title: '2. Data collected',
        body: ['We collect the following data when you use our services:'],
        list: [
          'Identity data: last name, first name, date of birth',
          'Contact data: email address, phone number',
          'Financial data: transaction history, payment method (not card numbers)',
          'Browsing data: IP address, device type, pages visited',
          'Gaming data: bets placed, history of stakes and winnings',
        ],
      },
      {
        title: '3. Purposes of processing',
        body: ['Your personal data is used for the following purposes:'],
        list: [
          'Creation and management of your account',
          'Processing of financial transactions',
          'Identity verification (KYC) in accordance with legal obligations',
          'Prevention of fraud and money laundering',
          'Improvement of our services and personalisation of the user experience',
          'Communication of information relating to your account',
        ],
      },
      {
        title: '4. Legal basis',
        body: [
          'The processing of your data is based on: (1) the performance of the contract binding us to you as a user, (2) compliance with legal obligations to which Roxs Sports is subject, (3) your explicit consent for marketing communications.',
        ],
      },
      {
        title: '5. Retention period',
        body: [
          'Your data is retained for the entire duration of your contractual relationship with Roxs Sports, and beyond for the legally required periods (generally 5 to 10 years for financial data).',
          'Browsing data is retained for a maximum of 13 months.',
        ],
      },
      {
        title: '6. Data sharing',
        body: ['Roxs Sports does not sell your personal data to third parties. We may share your data with:'],
        list: [
          'Our payment providers to process your transactions',
          'The competent authorities as part of legal obligations',
          'Our technical providers (hosting, security) under a confidentiality agreement',
        ],
      },
      {
        title: '7. Your rights (GDPR)',
        body: ['In accordance with the General Data Protection Regulation, you have the following rights:'],
        list: [
          'Right of access to your personal data',
          'Right to rectification of inaccurate data',
          'Right to erasure ("right to be forgotten")',
          'Right to portability of your data',
          'Right to object to processing',
          'Right to restriction of processing',
        ],
      },
      {
        title: '8. Cookies',
        body: [
          'We use technical cookies necessary for the operation of the app, as well as analytical cookies (anonymised) to improve our service.',
          'No third-party advertising cookie is used without your explicit consent.',
        ],
      },
      {
        title: '9. Security',
        body: [
          'Roxs Sports implements technical and organisational security measures to protect your data against any unauthorised access, loss or destruction. All exchanges are encrypted via TLS/HTTPS.',
        ],
      },
    ],
  },
  it: {
    backTitle: 'Informativa sulla privacy',
    heroTitle: 'Informativa sulla privacy',
    heroSub: 'Conformità GDPR — Aggiornamento: gennaio 2025',
    tag: 'Protezione dei dati — GDPR',
    footer: '© 2025 Roxs Sports — Tutti i diritti riservati',
    sections: [
      {
        title: '1. Titolare del trattamento',
        body: [
          'Roxs Sports è il titolare del trattamento dei tuoi dati personali, raccolti nell\'ambito dell\'uso della nostra piattaforma di scommesse sportive.',
          'Per qualsiasi domanda relativa alla protezione dei tuoi dati, puoi contattarci tramite la messaggistica di assistenza integrata nell\'app.',
        ],
      },
      {
        title: '2. Dati raccolti',
        body: ['Raccogliamo i seguenti dati durante l\'uso dei nostri servizi:'],
        list: [
          'Dati identificativi: cognome, nome, data di nascita',
          'Dati di contatto: indirizzo email, numero di telefono',
          'Dati finanziari: cronologia delle transazioni, metodo di pagamento (non i numeri di carta)',
          'Dati di navigazione: indirizzo IP, tipo di dispositivo, pagine visitate',
          'Dati di gioco: scommesse piazzate, cronologia di puntate e vincite',
        ],
      },
      {
        title: '3. Finalità del trattamento',
        body: ['I tuoi dati personali sono utilizzati per le seguenti finalità:'],
        list: [
          'Creazione e gestione del tuo account',
          'Elaborazione delle transazioni finanziarie',
          'Verifica dell\'identità (KYC) in conformità agli obblighi di legge',
          'Prevenzione delle frodi e del riciclaggio di denaro',
          'Miglioramento dei nostri servizi e personalizzazione dell\'esperienza utente',
          'Comunicazione di informazioni relative al tuo account',
        ],
      },
      {
        title: '4. Base giuridica',
        body: [
          'Il trattamento dei tuoi dati si basa su: (1) l\'esecuzione del contratto che ci lega a te come utente, (2) il rispetto degli obblighi di legge a cui Roxs Sports è soggetta, (3) il tuo consenso esplicito per le comunicazioni di marketing.',
        ],
      },
      {
        title: '5. Periodo di conservazione',
        body: [
          'I tuoi dati sono conservati per l\'intera durata del tuo rapporto contrattuale con Roxs Sports e, successivamente, per i periodi richiesti dalla legge (generalmente da 5 a 10 anni per i dati finanziari).',
          'I dati di navigazione sono conservati per un massimo di 13 mesi.',
        ],
      },
      {
        title: '6. Condivisione dei dati',
        body: ['Roxs Sports non vende i tuoi dati personali a terzi. Possiamo condividere i tuoi dati con:'],
        list: [
          'I nostri fornitori di pagamento per elaborare le tue transazioni',
          'Le autorità competenti nell\'ambito di obblighi di legge',
          'I nostri fornitori tecnici (hosting, sicurezza) con contratto di riservatezza',
        ],
      },
      {
        title: '7. I tuoi diritti (GDPR)',
        body: ['In conformità al Regolamento generale sulla protezione dei dati, disponi dei seguenti diritti:'],
        list: [
          'Diritto di accesso ai tuoi dati personali',
          'Diritto di rettifica dei dati inesatti',
          'Diritto alla cancellazione ("diritto all\'oblio")',
          'Diritto alla portabilità dei tuoi dati',
          'Diritto di opposizione al trattamento',
          'Diritto alla limitazione del trattamento',
        ],
      },
      {
        title: '8. Cookie',
        body: [
          'Utilizziamo cookie tecnici necessari al funzionamento dell\'app, nonché cookie analitici (anonimizzati) per migliorare il nostro servizio.',
          'Nessun cookie pubblicitario di terze parti viene utilizzato senza il tuo consenso esplicito.',
        ],
      },
      {
        title: '9. Sicurezza',
        body: [
          'Roxs Sports attua misure di sicurezza tecniche e organizzative per proteggere i tuoi dati da qualsiasi accesso non autorizzato, perdita o distruzione. Tutti gli scambi sono crittografati tramite TLS/HTTPS.',
        ],
      },
    ],
  },
  nl: {
    backTitle: 'Privacybeleid',
    heroTitle: 'Privacybeleid',
    heroSub: 'AVG-naleving — Bijgewerkt: januari 2025',
    tag: 'Gegevensbescherming — AVG',
    footer: '© 2025 Roxs Sports — Alle rechten voorbehouden',
    sections: [
      {
        title: '1. Verwerkingsverantwoordelijke',
        body: [
          'Roxs Sports is de verwerkingsverantwoordelijke voor uw persoonsgegevens, verzameld in het kader van het gebruik van ons sportweddenschappenplatform.',
          'Voor vragen over de bescherming van uw gegevens kunt u contact met ons opnemen via de in de app geïntegreerde supportberichten.',
        ],
      },
      {
        title: '2. Verzamelde gegevens',
        body: ['Wij verzamelen de volgende gegevens bij het gebruik van onze diensten:'],
        list: [
          'Identiteitsgegevens: achternaam, voornaam, geboortedatum',
          'Contactgegevens: e-mailadres, telefoonnummer',
          'Financiële gegevens: transactiegeschiedenis, betaalmethode (niet de kaartnummers)',
          'Browsegegevens: IP-adres, apparaattype, bezochte pagina\'s',
          'Spelgegevens: geplaatste weddenschappen, geschiedenis van inzetten en winsten',
        ],
      },
      {
        title: '3. Doeleinden van de verwerking',
        body: ['Uw persoonsgegevens worden voor de volgende doeleinden gebruikt:'],
        list: [
          'Aanmaken en beheren van uw account',
          'Verwerking van financiële transacties',
          'Identiteitsverificatie (KYC) in overeenstemming met wettelijke verplichtingen',
          'Preventie van fraude en witwassen van geld',
          'Verbetering van onze diensten en personalisatie van de gebruikerservaring',
          'Communicatie van informatie over uw account',
        ],
      },
      {
        title: '4. Rechtsgrond',
        body: [
          'De verwerking van uw gegevens is gebaseerd op: (1) de uitvoering van het contract dat ons als gebruiker aan u bindt, (2) de naleving van wettelijke verplichtingen waaraan Roxs Sports is onderworpen, (3) uw uitdrukkelijke toestemming voor marketingcommunicatie.',
        ],
      },
      {
        title: '5. Bewaartermijn',
        body: [
          'Uw gegevens worden bewaard gedurende de volledige duur van uw contractuele relatie met Roxs Sports, en daarna gedurende de wettelijk vereiste termijnen (over het algemeen 5 tot 10 jaar voor financiële gegevens).',
          'Browsegegevens worden maximaal 13 maanden bewaard.',
        ],
      },
      {
        title: '6. Gegevens delen',
        body: ['Roxs Sports verkoopt uw persoonsgegevens niet aan derden. Wij kunnen uw gegevens delen met:'],
        list: [
          'Onze betalingsproviders om uw transacties te verwerken',
          'De bevoegde autoriteiten in het kader van wettelijke verplichtingen',
          'Onze technische providers (hosting, beveiliging) onder een geheimhoudingsovereenkomst',
        ],
      },
      {
        title: '7. Uw rechten (AVG)',
        body: ['In overeenstemming met de Algemene Verordening Gegevensbescherming heeft u de volgende rechten:'],
        list: [
          'Recht op toegang tot uw persoonsgegevens',
          'Recht op rectificatie van onjuiste gegevens',
          'Recht op wissing ("recht om vergeten te worden")',
          'Recht op overdraagbaarheid van uw gegevens',
          'Recht van bezwaar tegen de verwerking',
          'Recht op beperking van de verwerking',
        ],
      },
      {
        title: '8. Cookies',
        body: [
          'Wij gebruiken technische cookies die nodig zijn voor de werking van de app, evenals analytische cookies (geanonimiseerd) om onze dienst te verbeteren.',
          'Er wordt geen advertentiecookie van derden gebruikt zonder uw uitdrukkelijke toestemming.',
        ],
      },
      {
        title: '9. Beveiliging',
        body: [
          'Roxs Sports implementeert technische en organisatorische beveiligingsmaatregelen om uw gegevens te beschermen tegen ongeoorloofde toegang, verlies of vernietiging. Alle uitwisselingen worden versleuteld via TLS/HTTPS.',
        ],
      },
    ],
  },
  tr: {
    backTitle: 'Gizlilik politikası',
    heroTitle: 'Gizlilik politikası',
    heroSub: 'GDPR uyumu — Güncelleme: Ocak 2025',
    tag: 'Veri koruma — GDPR',
    footer: '© 2025 Roxs Sports — Tüm hakları saklıdır',
    sections: [
      {
        title: '1. Veri sorumlusu',
        body: [
          'Roxs Sports, spor bahisleri platformumuzun kullanımı kapsamında toplanan kişisel verilerinizin veri sorumlusudur.',
          'Verilerinizin korunmasıyla ilgili her türlü soru için uygulamaya entegre destek mesajlaşması aracılığıyla bize ulaşabilirsiniz.',
        ],
      },
      {
        title: '2. Toplanan veriler',
        body: ['Hizmetlerimizi kullanırken aşağıdaki verileri topluyoruz:'],
        list: [
          'Kimlik verileri: soyadı, ad, doğum tarihi',
          'İletişim verileri: e-posta adresi, telefon numarası',
          'Finansal veriler: işlem geçmişi, ödeme yöntemi (kart numaraları değil)',
          'Gezinme verileri: IP adresi, cihaz türü, ziyaret edilen sayfalar',
          'Oyun verileri: yapılan bahisler, bahis ve kazanç geçmişi',
        ],
      },
      {
        title: '3. İşleme amaçları',
        body: ['Kişisel verileriniz aşağıdaki amaçlarla kullanılır:'],
        list: [
          'Hesabınızın oluşturulması ve yönetimi',
          'Finansal işlemlerin işlenmesi',
          'Yasal yükümlülüklere uygun olarak kimlik doğrulama (KYC)',
          'Dolandırıcılığın ve kara para aklamanın önlenmesi',
          'Hizmetlerimizin iyileştirilmesi ve kullanıcı deneyiminin kişiselleştirilmesi',
          'Hesabınızla ilgili bilgilerin iletilmesi',
        ],
      },
      {
        title: '4. Hukuki dayanak',
        body: [
          'Verilerinizin işlenmesi şunlara dayanır: (1) sizinle kullanıcı olarak bizi bağlayan sözleşmenin ifası, (2) Roxs Sports\'in tabi olduğu yasal yükümlülüklere uyum, (3) pazarlama iletişimleri için açık rızanız.',
        ],
      },
      {
        title: '5. Saklama süresi',
        body: [
          'Verileriniz, Roxs Sports ile olan sözleşmesel ilişkinizin tüm süresi boyunca ve sonrasında yasal olarak gereken süreler boyunca (finansal veriler için genellikle 5 ila 10 yıl) saklanır.',
          'Gezinme verileri en fazla 13 ay saklanır.',
        ],
      },
      {
        title: '6. Verilerin paylaşılması',
        body: ['Roxs Sports kişisel verilerinizi üçüncü taraflara satmaz. Verilerinizi şunlarla paylaşabiliriz:'],
        list: [
          'İşlemlerinizi işlemek için ödeme sağlayıcılarımız',
          'Yasal yükümlülükler kapsamında yetkili makamlar',
          'Gizlilik sözleşmesi kapsamındaki teknik sağlayıcılarımız (barındırma, güvenlik)',
        ],
      },
      {
        title: '7. Haklarınız (GDPR)',
        body: ['Genel Veri Koruma Yönetmeliği uyarınca aşağıdaki haklara sahipsiniz:'],
        list: [
          'Kişisel verilerinize erişim hakkı',
          'Yanlış verilerin düzeltilmesi hakkı',
          'Silme hakkı ("unutulma hakkı")',
          'Verilerinizin taşınabilirliği hakkı',
          'İşlemeye itiraz etme hakkı',
          'İşlemenin kısıtlanması hakkı',
        ],
      },
      {
        title: '8. Çerezler',
        body: [
          'Uygulamanın çalışması için gerekli teknik çerezleri ve hizmetimizi iyileştirmek için analitik çerezleri (anonimleştirilmiş) kullanıyoruz.',
          'Açık rızanız olmadan hiçbir üçüncü taraf reklam çerezi kullanılmaz.',
        ],
      },
      {
        title: '9. Güvenlik',
        body: [
          'Roxs Sports, verilerinizi yetkisiz erişime, kayba veya imhaya karşı korumak için teknik ve organizasyonel güvenlik önlemleri uygular. Tüm veri alışverişleri TLS/HTTPS ile şifrelenir.',
        ],
      },
    ],
  },
  el: {
    backTitle: 'Πολιτική απορρήτου',
    heroTitle: 'Πολιτική απορρήτου',
    heroSub: 'Συμμόρφωση με τον GDPR — Ενημέρωση: Ιανουάριος 2025',
    tag: 'Προστασία δεδομένων — GDPR',
    footer: '© 2025 Roxs Sports — Με επιφύλαξη παντός δικαιώματος',
    sections: [
      {
        title: '1. Υπεύθυνος επεξεργασίας',
        body: [
          'Η Roxs Sports είναι ο υπεύθυνος επεξεργασίας των προσωπικών σας δεδομένων, τα οποία συλλέγονται στο πλαίσιο της χρήσης της πλατφόρμας αθλητικού στοιχηματισμού μας.',
          'Για οποιαδήποτε ερώτηση σχετικά με την προστασία των δεδομένων σας, μπορείτε να επικοινωνήσετε μαζί μας μέσω της ενσωματωμένης στην εφαρμογή υπηρεσίας μηνυμάτων υποστήριξης.',
        ],
      },
      {
        title: '2. Δεδομένα που συλλέγονται',
        body: ['Συλλέγουμε τα ακόλουθα δεδομένα κατά τη χρήση των υπηρεσιών μας:'],
        list: [
          'Δεδομένα ταυτότητας: επώνυμο, όνομα, ημερομηνία γέννησης',
          'Δεδομένα επικοινωνίας: διεύθυνση email, αριθμός τηλεφώνου',
          'Οικονομικά δεδομένα: ιστορικό συναλλαγών, μέθοδος πληρωμής (όχι οι αριθμοί κάρτας)',
          'Δεδομένα περιήγησης: διεύθυνση IP, τύπος συσκευής, σελίδες που επισκεφθήκατε',
          'Δεδομένα παιχνιδιού: στοιχήματα που τοποθετήθηκαν, ιστορικό πονταρισμάτων και κερδών',
        ],
      },
      {
        title: '3. Σκοποί της επεξεργασίας',
        body: ['Τα προσωπικά σας δεδομένα χρησιμοποιούνται για τους ακόλουθους σκοπούς:'],
        list: [
          'Δημιουργία και διαχείριση του λογαριασμού σας',
          'Επεξεργασία των οικονομικών συναλλαγών',
          'Επαλήθευση ταυτότητας (KYC) σύμφωνα με τις νομικές υποχρεώσεις',
          'Πρόληψη της απάτης και του ξεπλύματος χρήματος',
          'Βελτίωση των υπηρεσιών μας και εξατομίκευση της εμπειρίας χρήστη',
          'Κοινοποίηση πληροφοριών σχετικά με τον λογαριασμό σας',
        ],
      },
      {
        title: '4. Νομική βάση',
        body: [
          'Η επεξεργασία των δεδομένων σας βασίζεται σε: (1) την εκτέλεση της σύμβασης που μας συνδέει με εσάς ως χρήστη, (2) τη συμμόρφωση με τις νομικές υποχρεώσεις στις οποίες υπόκειται η Roxs Sports, (3) τη ρητή συγκατάθεσή σας για επικοινωνίες μάρκετινγκ.',
        ],
      },
      {
        title: '5. Περίοδος διατήρησης',
        body: [
          'Τα δεδομένα σας διατηρούνται για όλη τη διάρκεια της συμβατικής σας σχέσης με τη Roxs Sports και πέραν αυτής για τις νομικά απαιτούμενες περιόδους (γενικά 5 έως 10 έτη για τα οικονομικά δεδομένα).',
          'Τα δεδομένα περιήγησης διατηρούνται για 13 μήνες κατ\' ανώτατο όριο.',
        ],
      },
      {
        title: '6. Κοινοποίηση δεδομένων',
        body: ['Η Roxs Sports δεν πουλά τα προσωπικά σας δεδομένα σε τρίτους. Ενδέχεται να κοινοποιήσουμε τα δεδομένα σας με:'],
        list: [
          'Τους παρόχους πληρωμών μας για την επεξεργασία των συναλλαγών σας',
          'Τις αρμόδιες αρχές στο πλαίσιο νομικών υποχρεώσεων',
          'Τους τεχνικούς μας παρόχους (φιλοξενία, ασφάλεια) βάσει σύμβασης εμπιστευτικότητας',
        ],
      },
      {
        title: '7. Τα δικαιώματά σας (GDPR)',
        body: ['Σύμφωνα με τον Γενικό Κανονισμό για την Προστασία Δεδομένων, έχετε τα ακόλουθα δικαιώματα:'],
        list: [
          'Δικαίωμα πρόσβασης στα προσωπικά σας δεδομένα',
          'Δικαίωμα διόρθωσης ανακριβών δεδομένων',
          'Δικαίωμα διαγραφής ("δικαίωμα στη λήθη")',
          'Δικαίωμα φορητότητας των δεδομένων σας',
          'Δικαίωμα εναντίωσης στην επεξεργασία',
          'Δικαίωμα περιορισμού της επεξεργασίας',
        ],
      },
      {
        title: '8. Cookies',
        body: [
          'Χρησιμοποιούμε τεχνικά cookies απαραίτητα για τη λειτουργία της εφαρμογής, καθώς και αναλυτικά cookies (ανωνυμοποιημένα) για τη βελτίωση της υπηρεσίας μας.',
          'Κανένα διαφημιστικό cookie τρίτων δεν χρησιμοποιείται χωρίς τη ρητή συγκατάθεσή σας.',
        ],
      },
      {
        title: '9. Ασφάλεια',
        body: [
          'Η Roxs Sports εφαρμόζει τεχνικά και οργανωτικά μέτρα ασφαλείας για την προστασία των δεδομένων σας από κάθε μη εξουσιοδοτημένη πρόσβαση, απώλεια ή καταστροφή. Όλες οι ανταλλαγές κρυπτογραφούνται μέσω TLS/HTTPS.',
        ],
      },
    ],
  },
  ro: {
    backTitle: 'Politica de confidențialitate',
    heroTitle: 'Politica de confidențialitate',
    heroSub: 'Conformitate GDPR — Actualizare: ianuarie 2025',
    tag: 'Protecția datelor — GDPR',
    footer: '© 2025 Roxs Sports — Toate drepturile rezervate',
    sections: [
      {
        title: '1. Operatorul de date',
        body: [
          'Roxs Sports este operatorul datelor dvs. cu caracter personal, colectate în cadrul utilizării platformei noastre de pariuri sportive.',
          'Pentru orice întrebare legată de protecția datelor dvs., ne puteți contacta prin mesageria de asistență integrată în aplicație.',
        ],
      },
      {
        title: '2. Datele colectate',
        body: ['Colectăm următoarele date în timpul utilizării serviciilor noastre:'],
        list: [
          'Date de identitate: nume, prenume, data nașterii',
          'Date de contact: adresă de e-mail, număr de telefon',
          'Date financiare: istoricul tranzacțiilor, metoda de plată (nu numerele cardului)',
          'Date de navigare: adresă IP, tip de dispozitiv, pagini vizitate',
          'Date de joc: pariuri plasate, istoricul mizelor și câștigurilor',
        ],
      },
      {
        title: '3. Scopurile prelucrării',
        body: ['Datele dvs. cu caracter personal sunt utilizate în următoarele scopuri:'],
        list: [
          'Crearea și gestionarea contului dvs.',
          'Procesarea tranzacțiilor financiare',
          'Verificarea identității (KYC) în conformitate cu obligațiile legale',
          'Prevenirea fraudei și a spălării banilor',
          'Îmbunătățirea serviciilor noastre și personalizarea experienței utilizatorului',
          'Comunicarea informațiilor referitoare la contul dvs.',
        ],
      },
      {
        title: '4. Temeiul juridic',
        body: [
          'Prelucrarea datelor dvs. se bazează pe: (1) executarea contractului care ne leagă de dvs. în calitate de utilizator, (2) respectarea obligațiilor legale cărora li se supune Roxs Sports, (3) consimțământul dvs. explicit pentru comunicările de marketing.',
        ],
      },
      {
        title: '5. Perioada de păstrare',
        body: [
          'Datele dvs. sunt păstrate pe toată durata relației dvs. contractuale cu Roxs Sports și ulterior pe perioadele impuse legal (în general de la 5 la 10 ani pentru datele financiare).',
          'Datele de navigare sunt păstrate maximum 13 luni.',
        ],
      },
      {
        title: '6. Partajarea datelor',
        body: ['Roxs Sports nu vinde datele dvs. cu caracter personal unor terți. Putem partaja datele dvs. cu:'],
        list: [
          'Furnizorii noștri de plăți pentru a procesa tranzacțiile dvs.',
          'Autoritățile competente în cadrul obligațiilor legale',
          'Furnizorii noștri tehnici (găzduire, securitate) în baza unui contract de confidențialitate',
        ],
      },
      {
        title: '7. Drepturile dvs. (GDPR)',
        body: ['În conformitate cu Regulamentul General privind Protecția Datelor, dispuneți de următoarele drepturi:'],
        list: [
          'Dreptul de acces la datele dvs. cu caracter personal',
          'Dreptul de rectificare a datelor inexacte',
          'Dreptul la ștergere ("dreptul de a fi uitat")',
          'Dreptul la portabilitatea datelor dvs.',
          'Dreptul de opoziție la prelucrare',
          'Dreptul la restricționarea prelucrării',
        ],
      },
      {
        title: '8. Cookie-uri',
        body: [
          'Utilizăm cookie-uri tehnice necesare funcționării aplicației, precum și cookie-uri analitice (anonimizate) pentru a ne îmbunătăți serviciul.',
          'Niciun cookie publicitar terț nu este utilizat fără consimțământul dvs. explicit.',
        ],
      },
      {
        title: '9. Securitate',
        body: [
          'Roxs Sports implementează măsuri de securitate tehnice și organizatorice pentru a vă proteja datele împotriva oricărui acces neautorizat, pierderi sau distrugeri. Toate schimburile sunt criptate prin TLS/HTTPS.',
        ],
      },
    ],
  },
};

export default function Privacy() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;
  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={c.backTitle} />
      <div className="legal">
        <div className="legal-hero">
          <div className="legal-hero-title">{c.heroTitle}</div>
          <div className="legal-hero-sub">{c.heroSub}</div>
        </div>

        <span className="legal-tag">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 2l3.5 6.5L22 10l-5 4.5 1 7L12 18l-6 3.5 1-7L2 10l6.5-1.5z" fill="currentColor" />
          </svg>
          {c.tag}
        </span>

        {c.sections.map((s) => (
          <div className="legal-section" key={s.title}>
            <div className="legal-section-title">{s.title}</div>
            {s.body.map((p, i) => <p className="legal-p" key={i}>{p}</p>)}
            {s.list && (
              <ul className="legal-ul">
                {s.list.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            )}
          </div>
        ))}

        <p className="legal-date">{c.footer}</p>
      </div>
    </PhoneShell>
  );
}
